import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// components
import { ReduxEntitySelect, ReduxInput, ReduxSelect } from 'components/FormElements'
// graphql
import { BUSINESS_UNITS_SELECT_QUERY } from 'graphql/businessUnit/queries'
// constants
import { AUTH_METHOD_OPTIONS } from 'constants/users'
import {
  TransactionName,
  ACTIONS_OPTIONS,
  JOB_TITLE_OPTIONS,
  PROPERTIES_OPTIONS,
  CONDITION_FIELD_OPTIONS
} from 'components/Modals/SyncRulesModal/components/Form/components/Rules/constants'
// types
import { Forms } from 'constants/forms'
import { WrappedFieldArrayProps } from 'redux-form/lib/FieldArray'
import { SystemProcessConditionAction } from 'types/graphql'
// views
import { AddButton, CloseIcon, FieldsWrapper, RulesWrapper, RuleWrapper } from './views'
// types
import { IOption } from 'components/FormElements/Select'
// utils
import { required } from 'utils/validators'
import { businessUnitProps } from 'components/FormElements/ReduxForm/ReduxEntitySelect/utils'

const getPropertyValuesField = (property: string, name: string) => {
  switch (property) {
    case TransactionName.AuthMethod: {
      return (
        <ReduxSelect name={name} validate={[required]} placeholder='Property Value' options={AUTH_METHOD_OPTIONS} />
      )
    }

    case TransactionName.BusinessUnit: {
      return (
        <ReduxEntitySelect
          name={name}
          validate={[required]}
          placeholder='Property Value'
          query={BUSINESS_UNITS_SELECT_QUERY}
          {...businessUnitProps}
        />
      )
    }

    case TransactionName.JobTitle: {
      return <ReduxSelect name={name} validate={[required]} placeholder='Property Value' options={JOB_TITLE_OPTIONS} />
    }
  }
}

interface RuleProps {
  field: string
  removeRule: () => void
}

const selector = formValueSelector(Forms.SYNC_RULES_FORM)

const Rule: React.FC<RuleProps> = ({ field, removeRule }) => {
  const dispatch = useDispatch()

  const fieldName = useSelector((state) => selector(state, `${field}.transactions[0].fieldName`))?.value
  const actionType = useSelector((state) => selector(state, `${field}.conditions[0].action`))?.value

  const isAlwaysTrue = actionType === SystemProcessConditionAction.AlwaysTrue

  const onActionChange = (value: IOption) => {
    if (value?.value === SystemProcessConditionAction.AlwaysTrue) {
      dispatch(
        change(Forms.SYNC_RULES_FORM, `${field}.transactions[0].fieldName`, {
          label: 'Job Title',
          value: TransactionName.JobTitle
        })
      )

      dispatch(change(Forms.SYNC_RULES_FORM, `${field}.conditions[0].fieldName`, null))
      dispatch(change(Forms.SYNC_RULES_FORM, `${field}.conditions[0].value.value`, null))
      dispatch(change(Forms.SYNC_RULES_FORM, `${field}.transactions[0].fieldValue`, null))
    } else {
      dispatch(change(Forms.SYNC_RULES_FORM, `${field}.transactions[0].fieldName`, null))
      dispatch(change(Forms.SYNC_RULES_FORM, `${field}.transactions[0].fieldValue`, null))
    }
  }

  return (
    <RuleWrapper>
      <FieldsWrapper>
        <span>Where</span>
        {!isAlwaysTrue && (
          <ReduxSelect
            validate={[required]}
            options={CONDITION_FIELD_OPTIONS}
            name={`${field}.conditions[0].fieldName`}
          />
        )}
        <ReduxSelect
          validate={[required]}
          options={ACTIONS_OPTIONS}
          onSelectChange={onActionChange}
          name={`${field}.conditions[0].action`}
        />
        {!isAlwaysTrue && <ReduxInput name={`${field}.conditions[0].value.value`} validate={[required]} />}
      </FieldsWrapper>
      <FieldsWrapper>
        <span>Set</span>
        <ReduxSelect
          validate={[required]}
          placeholder='Property'
          isDisabled={isAlwaysTrue}
          options={PROPERTIES_OPTIONS}
          name={`${field}.transactions[0].fieldName`}
        />
        {getPropertyValuesField(fieldName, `${field}.transactions[0].fieldValue`)}
      </FieldsWrapper>
      <CloseIcon onClick={removeRule} />
    </RuleWrapper>
  )
}

const Rules: React.FC<WrappedFieldArrayProps> = ({ fields }) => {
  const addRule = () => fields.push({})

  const removeRule = (idx: number) => () => fields.remove(idx)

  return (
    <RulesWrapper>
      <span>Mapping</span>
      {fields.map((field, idx) => (
        <Rule key={field} field={field} removeRule={removeRule(idx)} />
      ))}
      <AddButton onClick={addRule}>+ add rule</AddButton>
    </RulesWrapper>
  )
}

export default Rules
